/**
 * detailingWorkspaceApi — aggregator client for the Detailing Workspace screen.
 *
 * Falls back to per-resource calls if the aggregator endpoint is missing
 * (defensive — the aggregator is a Tier-2 ship; older deploys may not have it).
 */

import ApiCall from "./apicall";

export async function getDetailingWorkspace(jobId) {
  try {
    const r = await ApiCall.get(`/job/${jobId}/detailing-workspace`);
    return r.data;
  } catch (err) {
    if (err?.response?.status === 404) {
      // Fallback: assemble payload from per-resource calls
      return await assembleFromResources(jobId);
    }
    throw err;
  }
}

async function assembleFromResources(jobId) {
  // Best-effort assembly when aggregator isn't wired yet.
  const safeGet = async (path) => {
    try {
      const r = await ApiCall.get(path);
      return r.data;
    } catch (e) { return null; }
  };

  const [job, rfis, transmittals] = await Promise.all([
    safeGet(`/job/${jobId}`),
    safeGet(`/rfi?job_id=${jobId}&limit=5`),
    safeGet(`/transmittal?job_id=${jobId}&limit=5`),
  ]);

  const recentRfis = rfis?.rows || [];
  const recentTr = transmittals?.rows || [];

  return {
    job: job || { id: jobId, name: "(loading…)" },
    drawing_count: 0,
    detailer: null,
    kpis: {
      open_rfis: recentRfis.filter((r) => ["DRAFT", "SENT"].includes(r.status)).length,
      pending_transmittals: recentTr.filter((t) => t.status === "SENT").length,
      print_tickets_this_week: 0,
      outstanding_checks: 0,
      open_impacts: 0,
      active_waivers: 0,
    },
    recent_rfis: recentRfis,
    recent_transmittals: recentTr,
    workflow_state: { current: "JOB_CREATED", completed: [], pending: [], narrative: null },
  };
}

export const WORKFLOW_STEP_LABELS = {
  JOB_CREATED: "Job Created",
  DETAILER_ASSIGNED: "Detailer Assigned",
  DRAWINGS_COMPLETE: "Drawings Complete",
  CATEGORY_SET: "Category Set",
  AWAITING_CUSTOMER: "Awaiting Customer",
  APPROVED: "Approved",
  RELEASED_FOR_FAB: "Released for Fab",
};

export const WORKFLOW_STEPS = [
  "JOB_CREATED",
  "DETAILER_ASSIGNED",
  "DRAWINGS_COMPLETE",
  "CATEGORY_SET",
  "AWAITING_CUSTOMER",
  "APPROVED",
  "RELEASED_FOR_FAB",
];

/**
 * Set (override) the workflow step for a job.
 * CR-2026-FE-WORKFLOW-OVERRIDE 2026-05-05.
 *
 * Backend: POST /api/job/:id/detailing-workspace/workflow-step
 * Body: { step, reason }
 *
 * Writes an APPEND-ONLY audit_log entry with event_type
 * 'AISC-1-7-0:JOB-WORKFLOW-OVERRIDE'. The next GET reads the entry and
 * surfaces the override over the auto-computed state. V-03 immutability
 * is preserved at the DB-trigger layer.
 *
 * @param {number|string} jobId
 * @param {string} step  — one of WORKFLOW_STEPS
 * @param {string} reason — required, ≥4 chars
 * @returns {Promise<{ok: boolean, workflow_state: object|null, error: Error|null, status: number|null}>}
 */
export async function setWorkflowStep(jobId, step, reason) {
  if (!jobId && jobId !== 0) {
    return { ok: false, workflow_state: null, error: new Error("jobId required"), status: null };
  }
  if (!WORKFLOW_STEPS.includes(step)) {
    return { ok: false, workflow_state: null, error: new Error("Invalid step"), status: null };
  }
  const trimmed = (reason || "").trim();
  if (trimmed.length < 4) {
    return { ok: false, workflow_state: null, error: new Error("Reason required (min 4 chars)"), status: null };
  }
  try {
    const r = await ApiCall.post(
      `/job/${encodeURIComponent(jobId)}/detailing-workspace/workflow-step`,
      { step, reason: trimmed }
    );
    return {
      ok: true,
      workflow_state: r?.data?.workflow_state || null,
      error: null,
      status: r?.status || 200,
    };
  } catch (err) {
    return {
      ok: false,
      workflow_state: null,
      error: err,
      status: err?.response?.status || null,
    };
  }
}
